import React, { useState } from "react";
import { Grid, Button, MenuItem, FormControl, InputLabel, Select, CircularProgress, Stack } from "@mui/material";
import CustomSelect from "../CustomSelect";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EmailIcon from '@mui/icons-material/Email';
import { api } from "../../helpers/apiRequest";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import colors from "../../theme/Colors";
import { exportToPDF, exportToExcel, getPDFBlob } from "./ReportUtils";
import EmailReportDialog from "../EmailReportDialog";

const SalesReport = ({ customers, items, styles }) => {
  const [loadingSales, setLoadingSales] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedEmailCustomer, setSelectedEmailCustomer] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [salesFilters, setSalesFilters] = useState({
    item: items.length > 0 ? items[0]._id : "",
    style: styles.length > 0 ? styles[0]._id : "",
    customer: "",
    dateFrom: null,
    dateTo: null,
  });

  const getCustomerNameById = (customerId) => {
    const customer = customers.find(c => c._id === customerId);
    return customer ? customer.name : 'Unknown';
  };

  const generateSalesReport = async (exportType = 'html') => {
    if (!salesFilters.item || !salesFilters.style) {
      toast.error("Please select both Item and Style");
      return;
    }
    
    const params = new URLSearchParams();
    
    if (salesFilters.item) {
      params.append('item', salesFilters.item);
    }
    
    if (salesFilters.style) {
      params.append('style', salesFilters.style);
    }
    
    if (salesFilters.customer) {
      params.append('customer', salesFilters.customer);
    }
    
    if (salesFilters.dateFrom) {
      params.append('date_from', salesFilters.dateFrom.format("YYYY-MM-DD"));
    }
    
    if (salesFilters.dateTo) {
      params.append('date_to', salesFilters.dateTo.format("YYYY-MM-DD"));
    }
    
    const queryString = params.toString();
    
    setLoadingSales(true);
    try {
      const response = await api.get(
        `/reports/items-style-sales${queryString ? `?${queryString}` : ''}`
      );
      
      if (response.data && response.data.status) {
        const dateFromStr = salesFilters.dateFrom ? salesFilters.dateFrom.format("YYYY-MM-DD") : "All";
        const dateToStr = salesFilters.dateTo ? salesFilters.dateTo.format("YYYY-MM-DD") : "All";
        
        const selectedItem = salesFilters.item ? items.find(i => i._id === salesFilters.item)?.name : "All Items";
        const selectedStyle = salesFilters.style ? styles.find(s => s._id === salesFilters.style)?.name : "All Styles";
        const selectedCustomer = salesFilters.customer ? customers.find(c => c._id === salesFilters.customer)?.name : "All Customers";
        
        if (exportType === 'excel') {
          const excelData = response.data.data.map(item => ({
            "Item": item.item_name,
            "Style": item.style_name,
            "Customer": getCustomerNameById(item.customer),
            "Total Quantity": item.total_quantity
          }));
          
          exportToExcel(excelData, `Sales_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`);
          toast.success("Excel report generated successfully");
          return;
        }
        
        // let htmlContent = `
        //   <!DOCTYPE html>
        //   <html>
        //   <head>
        //     <title>Item Style Sales Report</title>
        //     <style>
        //       body { font-family: Arial, sans-serif; margin: 20px; }
        //       h1 { color: #1abc9c; }
        //       table { border-collapse: collapse; width: 100%; margin-top: 20px; }
        //       th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
        //       td { border: 1px solid #ddd; padding: 8px; }
        //       tr:nth-child(even) { background-color: #f2f2f2; }
        //       .report-header { margin-bottom: 20px; }
        //       .report-filters { margin-bottom: 15px; font-size: 14px; }
        //       .report-filters span { margin-right: 15px; }
        //       .print-btn { background-color: #1abc9c; color: white; border: none; padding: 10px 20px; 
        //                   cursor: pointer; border-radius: 4px; margin-top: 20px; margin-right: 10px; }
        //       .export-btn-container { display: flex; margin-top: 20px; }
        //     </style>
        //   </head>
        //   <body>
        //     <div class="report-header">
        //       <h1>Item Style Sales Report</h1>
        //       <div class="report-filters">
        //         <span><strong>Item:</strong> ${selectedItem}</span>
        //         <span><strong>Style:</strong> ${selectedStyle}</span>
        //         <span><strong>Customer:</strong> ${selectedCustomer}</span>
        //       </div>
        //       ${(salesFilters.dateFrom || salesFilters.dateTo) ? `<p>Date Range: ${dateFromStr} to ${dateToStr}</p>` : ''}
        //     </div>
            
        //     <table>
        //       <thead>
        //         <tr>
        //           <th>Item</th>
        //           <th>Style</th>
        //           <th>Customer</th>
        //           <th>Total Quantity</th>
        //         </tr>
        //       </thead>
        //       <tbody>
        // `;
        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Item Style Sales Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 15px; padding: 50px 30px; align-items:center;-webkit-user-select: text;-moz-user-select: text;-ms-user-select: text; user-select: text;  }
              h1 { color: #1abc9c; text-align: center; }
              table { border-collapse: collapse; width: 95%; margin: 15px; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin: 15px; }
              .report-filters { font-size: 14px; }
              .print-btn { background-color: #1abc9c; color: white; border: none; padding: 10px 20px; 
                          cursor: pointer; border-radius: 4px; margin-top: 20px; margin-right: 10px; }
              .export-btn-container { display: flex; margin-top: 20px; }
            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>Item Style Sales Report</h1>
              <div class="report-filters">
                <span><strong>Item:</strong> ${selectedItem}</span>
                <span><strong>Style:</strong> ${selectedStyle}</span>
                <span><strong>Customer:</strong> ${selectedCustomer}</span>
              </div>
              ${(salesFilters.dateFrom || salesFilters.dateTo) ? `<p>Date Range: ${dateFromStr} to ${dateToStr}</p>` : ''}
            </div>
            
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Style</th>
                  <th>Customer</th>
                  <th>Total Quantity</th>
                </tr>
              </thead>
              <tbody>
        `;
        response.data.data.forEach(item => {
          htmlContent += `
            <tr>
              <td>${item.item_name}</td>
              <td>${item.style_name}</td>
              <td>${getCustomerNameById(item.customer)}</td>
              <td>${item.total_quantity}</td>
            </tr>
          `;
        });
        
        htmlContent += `
              </tbody>
            </table>
            
            <div class="export-btn-container">
            </div>
          </body>
          </html>
        `;
        
        if (exportType === 'pdf') {
          exportToPDF(htmlContent, `Sales_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`);
          toast.success("PDF report generated successfully");
          return;
        }
        
        if (exportType === 'email') {
          setEmailDialogOpen(true);
          setLoadingSales(false);
          return;
        }
        
        const reportWindow = window.open('', '_blank');
        if (!reportWindow) {
          toast.error("Please allow popups for this site to view reports");
          return;
        }
        
        reportWindow.document.write(htmlContent);
        reportWindow.document.close();
        
        window.addEventListener('message', function(event) {
          if (event.data.type === 'exportPDF' && event.data.reportType === 'sales') {
            generateSalesReport('pdf');
          } else if (event.data.type === 'exportExcel' && event.data.reportType === 'sales') {
            generateSalesReport('excel');
          }
        });
        
        toast.success("Report generated successfully");
      } else {
        toast.error("No data available for the selected filters");
      }
    } catch (error) {
      console.error("Error generating sales report:", error);
      toast.error("Failed to generate sales report");
    } finally {
      setLoadingSales(false);
    }
  };

  const sendReportByEmail = async () => {
    if (!selectedEmailCustomer) {
      toast.error('Please select a customer');
      return;
    }

    setSendingEmail(true);
    try {
      // Generate the report HTML
      const params = new URLSearchParams();
      if (salesFilters.item) params.append('item', salesFilters.item);
      if (salesFilters.style) params.append('style', salesFilters.style);
      if (salesFilters.customer) params.append('customer', salesFilters.customer);
      if (salesFilters.dateFrom) params.append('date_from', salesFilters.dateFrom.format("YYYY-MM-DD"));
      if (salesFilters.dateTo) params.append('date_to', salesFilters.dateTo.format("YYYY-MM-DD"));
      
      const queryString = params.toString();
      
      const response = await api.get(`/reports/items-style-sales${queryString ? `?${queryString}` : ''}`);
      
      if (response.data && response.data.status) {
        const dateFromStr = salesFilters.dateFrom ? salesFilters.dateFrom.format("YYYY-MM-DD") : "All";
        const dateToStr = salesFilters.dateTo ? salesFilters.dateTo.format("YYYY-MM-DD") : "All";
        
        const selectedItem = salesFilters.item ? items.find(i => i._id === salesFilters.item)?.name : "All Items";
        const selectedStyle = salesFilters.style ? styles.find(s => s._id === salesFilters.style)?.name : "All Styles";
        const selectedCustomer = salesFilters.customer ? customers.find(c => c._id === salesFilters.customer)?.name : "All Customers";
        
        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Item Style Sales Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              h1 { color: #1abc9c; }
              table { border-collapse: collapse; width: 100%; margin-top: 20px; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin-bottom: 20px; }
              .report-filters { margin-bottom: 15px; font-size: 14px; }
              .report-filters span { margin-right: 15px; }
            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>Item Style Sales Report</h1>
              <div class="report-filters">
                <span><strong>Item:</strong> ${selectedItem}</span>
                <span><strong>Style:</strong> ${selectedStyle}</span>
                <span><strong>Customer:</strong> ${selectedCustomer}</span>
              </div>
              ${(salesFilters.dateFrom || salesFilters.dateTo) ? `<p>Date Range: ${dateFromStr} to ${dateToStr}</p>` : ''}
            </div>
            
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Style</th>
                  <th>Customer</th>
                  <th>Total Quantity</th>
                </tr>
              </thead>
              <tbody>
        `;
        
        response.data.data.forEach(item => {
          htmlContent += `
            <tr>
              <td>${item.item_name}</td>
              <td>${item.style_name}</td>
              <td>${getCustomerNameById(item.customer)}</td>
              <td>${item.total_quantity}</td>
            </tr>
          `;
        });
        
        htmlContent += `
              </tbody>
            </table>
          </body>
          </html>
        `;
        
        // Convert HTML to PDF blob
        const pdfBlob = await getPDFBlob(htmlContent);
        
        // Create FormData and append the PDF blob
        const formData = new FormData();
        formData.append('image', pdfBlob, `Sales_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.pdf`);
        formData.append('userId', selectedEmailCustomer);
        formData.append('type', "Report");
        // Send the email
        const emailResponse = await api.post('inventory/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (emailResponse.status === 200) {
          toast.success('Report sent by email successfully');
          setEmailDialogOpen(false);
          setSelectedEmailCustomer('');
        } else {
          toast.error('Failed to send report by email');
        }
      } else {
        toast.error('No data available for the selected filters');
      }
    } catch (error) {
      console.error('Error sending report by email:', error);
      toast.error('Failed to send report by email');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <Grid container spacing={3} alignItems="center">
      <Grid item xs={12} sm={6} md={2}>
        <FormControl fullWidth>
          <InputLabel id="item-select-label">Item</InputLabel>
          <Select
            labelId="item-select-label"
            value={salesFilters.item}
            label="Item"
            onChange={(e) => setSalesFilters({ ...salesFilters, item: e.target.value })}
          >
            <MenuItem value="">All Items</MenuItem>
            {items.map((item) => (
              <MenuItem key={item._id} value={item._id}>
                {item.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <FormControl fullWidth>
          <InputLabel id="style-select-label">Style</InputLabel>
          <Select
            labelId="style-select-label"
            value={salesFilters.style}
            label="Style"
            onChange={(e) => setSalesFilters({ ...salesFilters, style: e.target.value })}
          >
            <MenuItem value="">All Styles</MenuItem>
            {styles.map((style) => (
              <MenuItem key={style._id} value={style._id}>
                {style.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <CustomSelect
          label="Customer"
          value={salesFilters.customer}
          onChange={(e) => setSalesFilters({ ...salesFilters, customer: e.target.value })}
          options={[
            { id: "", name: "All Customers" },
            ...customers.map((customer) => ({
              id: customer._id,
              name: customer.name,
            })),
          ]}
          fullWidth
          sx={{ minWidth: "100%" }}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <DatePicker
          label="From Date"
          value={salesFilters.dateFrom}
          onChange={(newValue) => setSalesFilters({ ...salesFilters, dateFrom: newValue })}
          slotProps={{ textField: { fullWidth: true } }}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <DatePicker
          label="To Date"
          value={salesFilters.dateTo}
          onChange={(newValue) => setSalesFilters({ ...salesFilters, dateTo: newValue })}
          slotProps={{ textField: { fullWidth: true } }}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <Stack spacing={1}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => generateSalesReport('html')}
            disabled={loadingSales || sendingEmail}
            startIcon={<DescriptionIcon />}
            sx={{ 
              height: "38px", 
              backgroundColor: colors.primary, 
              '&:hover': { 
                backgroundColor: colors.primary, 
                opacity: 0.9 
              } 
            }}
          >
            {loadingSales ? <CircularProgress size={24} color="inherit" /> : "View"}
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateSalesReport('pdf')}
            disabled={loadingSales || sendingEmail}
            startIcon={<PictureAsPdfIcon />}
            sx={{ 
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': { 
                borderColor: colors.primary,
                opacity: 0.9 
              } 
            }}
          >
            PDF
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateSalesReport('excel')}
            disabled={loadingSales || sendingEmail}
            startIcon={<FileDownloadIcon />}
            sx={{ 
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': { 
                borderColor: colors.primary,
                opacity: 0.9 
              } 
            }}
          >
            Excel
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateSalesReport('email')}
            disabled={loadingSales || sendingEmail}
            startIcon={<EmailIcon />}
            sx={{ 
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': { 
                borderColor: colors.primary,
                opacity: 0.9 
              } 
            }}
          >
            Email
          </Button>
        </Stack>
      </Grid>

      <EmailReportDialog
        open={emailDialogOpen}
        handleClose={() => {
          setEmailDialogOpen(false);
          setSelectedEmailCustomer('');
        }}
        selectedCustomer={selectedEmailCustomer}
        setSelectedCustomer={setSelectedEmailCustomer}
        customers={customers}
        isLoading={sendingEmail}
        handleSendEmail={sendReportByEmail}
      />
    </Grid>
  );
};

export default SalesReport;
